// ========================================
// Recent Activity Component
// ========================================
// Shows recent check-in summary

import { format } from 'date-fns';
import { CheckIn } from '../../types';
import { getEmotionColor, LIFE_CATEGORIES } from '../../utils/emotionData';

interface RecentActivityProps {
  recentCheckIn: CheckIn | null;
  loading: boolean;
}

const RecentActivity = ({ recentCheckIn, loading }: RecentActivityProps) => {
  if (loading) {
    return (
      <div className="recent-activity-card glass-panel">
        <p className="loading-text">Loading recent activity...</p>
      </div>
    );
  }

  if (!recentCheckIn) {
    return (
      <div className="recent-activity-card glass-panel">
        <p className="empty-state">No check-ins yet. Start your first check-in to track your emotions!</p>
      </div>
    );
  }

  const timestamp = recentCheckIn.timestamp;
  const formattedDate = timestamp
    ? format(
        typeof timestamp === 'object' && 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp),
        'MMM d, yyyy h:mm a'
      )
    : 'Unknown date';

  // Count total emotions
  const totalEmotions = Object.values(recentCheckIn.emotions).reduce(
    (sum, emotions) => sum + (emotions?.length || 0),
    0
  );

  return (
    <div className="recent-activity-card glass-panel">
      <div className="activity-header">
        <h3>Last Check-in</h3>
        <span className="activity-date">{formattedDate}</span>
      </div>

      <div className="activity-summary">
        <p className="activity-stat">
          <strong>{totalEmotions}</strong> emotions tracked across{' '}
          <strong>{Object.keys(recentCheckIn.emotions).length}</strong> life categories
        </p>
      </div>

      {recentCheckIn.notes && (
        <div className="activity-notes">
          <p className="notes-label">Notes:</p>
          <p className="notes-text">{recentCheckIn.notes}</p>
        </div>
      )}

      <div className="activity-categories">
        {LIFE_CATEGORIES.map((category) => {
          const categoryKey = category.toLowerCase() as keyof typeof recentCheckIn.emotions;
          const emotions = recentCheckIn.emotions[categoryKey];

          if (!emotions || emotions.length === 0) return null;

          return (
            <div key={category} className="category-badge">
              <span className="category-name">{category}</span>
              <div className="emotion-dots">
                {emotions.map((emotion, index) => (
                  <span
                    key={index}
                    className="emotion-dot"
                    style={{ backgroundColor: getEmotionColor(emotion) }}
                    title={emotion}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivity;
